<template>
  <div class="reward-screen cb-page">
    <div class="reward-screen__top">
      <StoragePill variant="inline" :count="balance" :from="balanceFrom" animate />
    </div>

    <section class="reward-hero" aria-labelledby="reward-title">
      <div class="reward-hero__rays" aria-hidden="true"></div>
      <div class="reward-hero__ring" aria-hidden="true"></div>
      <ConfettiBurst class="reward-hero__confetti" loop :count="40" :spread="200" :duration="2200" />

      <div id="reward-title" class="reward-star" role="img" :aria-label="`Награда: плюс ${reward} звёзд`">
        <svg class="reward-star__svg" viewBox="0 0 200 200" aria-hidden="true">
          <defs>
            <linearGradient id="reward-star-body" x1="0.2" y1="0" x2="0.8" y2="1">
              <stop offset="0%" stop-color="#FFF6A0" />
              <stop offset="38%" stop-color="#FBE04A" />
              <stop offset="75%" stop-color="#F2C531" />
              <stop offset="100%" stop-color="#D9A21E" />
            </linearGradient>
            <linearGradient id="reward-star-bevel" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#FFFBD6" stop-opacity="0.95" />
              <stop offset="55%" stop-color="#FFE873" stop-opacity="0.35" />
              <stop offset="100%" stop-color="#FFE873" stop-opacity="0" />
            </linearGradient>
            <radialGradient id="reward-star-shine" cx="0.38" cy="0.3" r="0.45">
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.75" />
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
            </radialGradient>
          </defs>
          <path class="reward-star__shadow" :d="STAR" transform="translate(0 7)" />
          <path class="reward-star__body" :d="STAR" />
          <path class="reward-star__bevel" :d="STAR" transform="translate(100 100) scale(0.8) translate(-100 -96)" />
          <ellipse cx="78" cy="70" rx="34" ry="20" fill="url(#reward-star-shine)" transform="rotate(-24 78 70)" />
        </svg>
        <!-- Один слой текста: обводка рисуется под заливкой (paint-order), мягкая тень — одним фильтром. -->
        <svg class="reward-star__value" viewBox="0 0 200 200" aria-hidden="true">
          <defs>
            <filter id="reward-value-shadow" x="-15%" y="-30%" width="130%" height="170%" color-interpolation-filters="sRGB">
              <feDropShadow dx="0" dy="2.5" stdDeviation="1.6" flood-color="#6E3204" flood-opacity="0.55" />
            </filter>
          </defs>
          <text
            class="reward-star__value-text cb-num"
            x="100"
            y="132"
            text-anchor="middle"
            filter="url(#reward-value-shadow)"
          >+{{ reward }}</text>
        </svg>
      </div>

      <div class="reward-hero__pill">Вы заработали!</div>
    </section>

    <ProfileCard
      class="reward-screen__profile"
      :player="session.me"
      avatar-shape="rounded"
      avatar-corners
      editable
      @edit="router.push('/profile')"
    />

    <div class="cb-page__footer">
      <AppButton block @click="finish">Продолжить</AppButton>
    </div>
  </div>
</template>

<script setup>
/**
 * RewardScreen (/reward) — макет 8 «Награда»: «Хранилище» с новым балансом (досчёт),
 * большая светящаяся звезда «+<награда>» (SVG/CSS) с лучами и конфетти, «Вы заработали!»,
 * карточка профиля (обновляется через session.loadMe()), «Продолжить» → «/».
 */
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import ConfettiBurst from "@/components/ui/ConfettiBurst.vue";
import ProfileCard from "@/components/ui/ProfileCard.vue";
import StoragePill from "@/components/ui/StoragePill.vue";
import telegram from "@/services/telegram";
import { useGameStore } from "@/stores/game";
import { useSessionStore } from "@/stores/session";
import { useGameOutcome } from "./game/useGameOutcome";

// Пятиконечная звезда (скругление углов — толстой обводкой с round-join).
const STAR = "M100 18 L124 70 L180 76 L138 114 L150 170 L100 141 L50 170 L62 114 L20 76 L76 70 Z";

const router = useRouter();
const game = useGameStore();
const session = useSessionStore();
const { reward } = useGameOutcome();

const balance = computed(() => {
  if (session.me && Number.isFinite(Number(session.me.stars))) return Number(session.me.stars);
  return Number(game.stars) || 0;
});
const balanceFrom = computed(() => Math.max(0, balance.value - reward.value));

function finish() {
  router.replace("/");
}

onMounted(() => {
  telegram.haptic("success");
  session.loadMe().catch(() => null);
});
</script>

<style scoped>
.reward-screen {
  gap: 14px;
  /* Лучи и кольцо шире экрана — без горизонтальной прокрутки. */
  overflow-x: hidden;
  overflow-x: clip;
}

.reward-screen__top {
  display: flex;
  justify-content: center;
  padding-top: 6px;
  position: relative;
  z-index: 2;
}

/* ------------------------------------------------------------- hero */

.reward-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 300px;
  padding-bottom: 6px;
}

.reward-hero__rays {
  position: absolute;
  left: 50%;
  top: 46%;
  width: 150%;
  max-width: 620px;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: repeating-conic-gradient(from 0deg, rgba(221, 245, 90, 0.16) 0deg 5deg, rgba(221, 245, 90, 0) 5deg 15deg);
  -webkit-mask-image: radial-gradient(circle, #000 12%, rgba(0, 0, 0, 0.5) 34%, transparent 62%);
  mask-image: radial-gradient(circle, #000 12%, rgba(0, 0, 0, 0.5) 34%, transparent 62%);
  animation: reward-rays 40s linear infinite;
  pointer-events: none;
}

.reward-hero__ring {
  position: absolute;
  left: 50%;
  top: 46%;
  width: min(84vw, 350px);
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(221, 245, 90, 0.35);
  box-shadow: 0 0 24px rgba(221, 245, 90, 0.25), inset 0 0 40px rgba(221, 245, 90, 0.12);
  background: radial-gradient(circle, rgba(221, 245, 90, 0.22) 0%, rgba(221, 245, 90, 0.06) 45%, rgba(221, 245, 90, 0) 70%);
  pointer-events: none;
}

.reward-hero__confetti {
  top: -4%;
}

.reward-star {
  position: relative;
  z-index: 1;
  width: min(72vw, 300px);
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: reward-pop 0.7s var(--cb-ease) both, reward-float 3.2s ease-in-out 0.7s infinite;
}

.reward-star__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  filter: drop-shadow(0 0 22px rgba(246, 214, 70, 0.75)) drop-shadow(0 0 60px rgba(221, 245, 90, 0.45));
}

.reward-star__shadow {
  fill: #a8780f;
  stroke: #a8780f;
  stroke-width: 22;
  stroke-linejoin: round;
}

.reward-star__body {
  fill: url(#reward-star-body);
  stroke: url(#reward-star-body);
  stroke-width: 22;
  stroke-linejoin: round;
}

.reward-star__bevel {
  fill: url(#reward-star-bevel);
  stroke: url(#reward-star-bevel);
  stroke-width: 10;
  stroke-linejoin: round;
  opacity: 0.8;
}

/* «+40» — SVG-текст в координатах звезды (масштабируется вместе с ней). Обводка под заливкой
   (paint-order: stroke) — внутренние контуры вариативного шрифта не видны, слой текста один. */
.reward-star__value {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.reward-star__value-text {
  font-family: var(--cb-font);
  font-size: 64px;
  font-weight: 800;
  letter-spacing: -0.02em;
  fill: #fffbe6;
  stroke: #a2500c;
  stroke-width: 5px;
  stroke-linejoin: round;
  paint-order: stroke fill;
}

.reward-hero__pill {
  position: relative;
  z-index: 2;
  margin-top: -6px;
  padding: 16px 36px;
  border-radius: var(--cb-radius-lg);
  background: linear-gradient(180deg, #e9fb7a 0%, var(--cb-lime) 55%, var(--cb-lime-press) 100%);
  color: var(--cb-on-lime);
  font-size: clamp(22px, 7vw, 28px);
  font-weight: 800;
  letter-spacing: -0.01em;
  box-shadow: 0 0 30px rgba(221, 245, 90, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.6), inset 0 -3px 0 rgba(0, 0, 0, 0.08);
  animation: cb-rise-in 0.5s var(--cb-ease) 0.35s both;
}

.reward-screen__profile {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

@keyframes reward-rays {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes reward-pop {
  0% {
    opacity: 0;
    transform: scale(0.4) rotate(-12deg);
  }
  65% {
    opacity: 1;
    transform: scale(1.08) rotate(3deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

@keyframes reward-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

@media (max-height: 700px) {
  .reward-hero {
    min-height: 240px;
  }

  .reward-star {
    width: min(52vw, 210px);
  }
}
</style>
